// Senderverbund: Übersicht (wer bekommt was wann, Status je Sender), Sender (anlegen/kopieren/an-aus),
// Übernahmeregeln (Wochenraster, Sendung, Zeitversatz) und Sendenachweis je Sender.

import { Fragment, useEffect, useMemo, useState } from "react";
import { api, toast, useLoad, usePoll } from "../../api";
import { Empty, Field, HowTo, Modal, PageHead, Switch, Tip, useMe } from "../../ui";

const DAYS = ["So", "Mo", "Di", "Mi", "Do", "Fr", "Sa"];
const ORDER = [1, 2, 3, 4, 5, 6, 0];
const hhmm = (m: number) => `${String(Math.floor(m / 60) % 24).padStart(2, "0")}:${String(m % 60).padStart(2, "0")}`;
const toMin = (s: string) => { const [h, m] = s.split(":").map(Number); return (h || 0) * 60 + (m || 0); };
const shiftText = (m: number) => (!m ? "zeitgleich" : `${Math.abs(m) >= 60 ? `${Math.floor(Math.abs(m) / 60)} h ` : ""}${Math.abs(m) % 60 ? `${Math.abs(m) % 60} min ` : ""}${m > 0 ? "später" : "früher"}`.trim());

/** Selected station (cookie rc_station; the API follows it for every request, links and audio included). */
export function currentStation(): string {
  const m = /(?:^|;\s*)rc_station=([^;]+)/.exec(document.cookie);
  return m ? decodeURIComponent(m[1]) : "main";
}
/** Switch station and open a page there. */
export function switchStationTo(id: string, hash: string) {
  document.cookie = `rc_station=${encodeURIComponent(id)}; path=/; max-age=31536000; SameSite=Strict; Secure`;
  window.location.hash = hash;
  window.location.reload();
}
export function switchStation(id: string) {
  document.cookie = `rc_station=${encodeURIComponent(id)}; path=/; max-age=31536000; SameSite=Strict; Secure`;
  window.location.reload();
}

export function StationSelect() {
  const { data } = useLoad<any[]>("/api/stations");
  const cur = currentStation();
  if (!data?.length) return null;
  return (
    <select value={data.some((s) => s.id === cur) ? cur : data[0].id} onChange={(e) => switchStation(e.target.value)} title="Alle Seiten zeigen diesen Sender">
      {data.map((s) => <option key={s.id} value={s.id}>{s.name}{s.enabled ? "" : " (aus)"}</option>)}
    </select>
  );
}

function Overview() {
  const { data: status } = usePoll<any[]>("/api/stations/status", 10_000);
  const { data: stations } = useLoad<any[]>("/api/stations");
  const { data: rules } = useLoad<any[]>("/api/syndication/rules");
  const color = (id: string) => stations?.find((s) => s.id === id)?.color ?? "#888";
  return (
    <div className="stack">
      <div className="grid g3">
        {(status ?? []).map((s) => {
          const info = stations?.find((x) => x.id === s.id);
          const ok = s.kind === "verbund" || s.engine === "läuft";
          return (
            <div key={s.id} className="card" style={{ borderTop: `4px solid ${color(s.id)}`, padding: 12 }}>
              <div className="row"><b>{s.name}</b><span className="spacer" /><span className={`badge ${!s.enabled ? "" : ok ? "ok" : "bad"}`}>{s.enabled ? s.engine : "aus"}</span></div>
              <div className="small" style={{ minHeight: 18 }}>{s.on_air || <span className="muted">–</span>}</div>
              {s.takeover && (
                <div className="small" style={{ margin: "4px 0", padding: "4px 8px", borderRadius: 6, background: s.takeover.active ? "var(--ok-2)" : "var(--paper)" }} title={s.takeover.why}>
                  <b>{s.takeover.active ? "⇄ " : "● "}{s.takeover.text}</b><span className="muted"> · {s.takeover.why}</span>
                  {s.lag_min != null && Math.abs(s.lag_min) >= 3 && <span style={{ color: "var(--warn)" }}> · {s.lag_min > 0 ? `${s.lag_min} min hinter` : `${-s.lag_min} min vor`} dem Plan</span>}
                </div>
              )}
              {s.kind === "verbund" && s.takers?.length > 0 && <div className="small muted">übernommen von: {s.takers.join(", ")}</div>}
              <div className="small muted">{info?.listeners ?? 0} Hörer · {s.outputs.filter((o: any) => o.state === "verbunden").length}/{s.outputs.length} Ausgänge {s.silence ? "· ⚠ Stille" : ""}</div>
              <div className="row wrap small" style={{ gap: 4, marginTop: 4 }}>
                {Object.entries(s.packaging ?? {}).map(([role, n]: any) => <span key={role} className={`badge ${n ? "" : "warn"}`}>{({ dropper: "Dropper", jingle: "Jingles", sweeper: "Sweeper", id: "IDs", promo: "Promos", ad: "Spots" } as any)[role] ?? role}: {n}</span>)}
              </div>
              {s.packaging_missing?.length > 0 && <div className="small" style={{ color: "var(--warn)" }}>⚠ Uhren brauchen {s.packaging_missing.join(", ")} – dieser Sender hat dafür noch keine Clips</div>}
              <div className="row" style={{ marginTop: 6 }}><span className="spacer" /><button className="btn small" onClick={() => switchStation(s.id)}>Zu diesem Sender</button></div>
            </div>
          );
        })}
      </div>
      <div className="card">
        <h2>Wer bekommt was wann</h2>
        {!rules?.length ? <Empty>Noch keine Übernahmeregeln. Unter „Regeln“ anlegen.</Empty> : (
          <div style={{ overflowX: "auto" }}>
            <table className="list" style={{ minWidth: 760 }}>
              <thead><tr><th>Zielsender</th>{ORDER.map((d) => <th key={d}>{DAYS[d]}</th>)}</tr></thead>
              <tbody>
                {[...new Set(rules.map((r) => r.target_station_id))].map((t) => (
                  <tr key={t}>
                    <td><span style={{ color: color(t) }}>■</span> {rules.find((r) => r.target_station_id === t)?.target_name}</td>
                    {ORDER.map((d) => (
                      <td key={d} style={{ verticalAlign: "top" }}>
                        <div style={{ position: "relative", height: 48, background: "var(--paper-2, #f2f0ea)", borderRadius: 4 }}>
                          {rules.filter((r) => r.target_station_id === t && r.enabled && !r.program_id && r.days.includes(d)).map((r) => (
                            <div key={r.id} title={`${r.name || ""} ${hhmm(r.start_min)}–${hhmm(r.end_min)} von ${r.source_name} (${shiftText(r.shift_min)})`}
                              style={{ position: "absolute", left: 0, right: 0, top: `${(r.start_min / 1440) * 100}%`, height: `${Math.max(4, ((r.end_min - r.start_min) / 1440) * 100)}%`, background: color(r.source_station_id), opacity: 0.85, borderRadius: 3 }} />
                          ))}
                        </div>
                      </td>
                    ))}
                  </tr>
                ))}
              </tbody>
            </table>
            <p className="small muted">Farbe = Quellsender, Höhe = Tageszeit (oben 0 Uhr, unten 24 Uhr). Regeln für einzelne Sendungen stehen unter „Regeln“.</p>
          </div>
        )}
      </div>
    </div>
  );
}

function Stations() {
  const me = useMe();
  const admin = me?.role === "admin";
  const { data, reload } = useLoad<any[]>("/api/stations");
  const [add, setAdd] = useState(false);
  const [nv, setNv] = useState<any>({ name: "", timezone: "Europe/Berlin", copy_from: "main", follow: true, copy: ["music", "clocks", "settings"], color: "#2f6fb1" });
  const [busy, setBusy] = useState(false);
  const toggle = (k: string) => setNv({ ...nv, copy: nv.copy.includes(k) ? nv.copy.filter((x: string) => x !== k) : [...nv.copy, k] });
  const create = async () => {
    setBusy(true);
    try {
      const r = await api("/api/stations", { body: nv });
      toast(`Sender „${nv.name}“ angelegt – die Engine startet in wenigen Sekunden`);
      setAdd(false);
      reload();
      void r;
    } catch (e: any) {
      toast(e.message, true);
    } finally {
      setBusy(false);
    }
  };
  return (
    <div className="card">
      <div className="row" style={{ marginBottom: 8 }}><h2 style={{ margin: 0 }}>Sender</h2><span className="spacer" />{admin && <button className="btn primary" onClick={() => setAdd(true)}>+ Sender</button>}</div>
      <Tip>Jeder Sender hat eine eigene Engine, eigene Ausgänge und eigene Verpackung. Die Bibliothek ist gemeinsam. Ausgänge (Mounts) trägst du nach dem Umschalten auf den Sender unter Einstellungen › Ausgänge ein.</Tip>
      <table className="list">
        <thead><tr><th>Sender</th><th>Kürzel</th><th>Zeitzone</th><th>Hörer</th><th>Ausgänge</th><th>An</th><th /></tr></thead>
        <tbody>
          {(data ?? []).map((s) => (
            <tr key={s.id}>
              <td><input type="color" disabled={!admin} value={s.color} onChange={async (e) => { await api(`/api/stations/${s.id}`, { method: "PUT", body: { color: e.target.value } }); reload(); }} style={{ width: 28, height: 22, padding: 0, border: 0 }} /> <b>{s.name}</b></td>
              <td className="mono small">{s.slug}</td>
              <td className="small">{s.timezone}</td>
              <td>{s.listeners}</td>
              <td>{s.outputs}</td>
              <td>{s.id === "main" ? <span className="small muted">immer</span> : <Switch checked={s.enabled} onChange={async (x) => { if (!admin) return; await api(`/api/stations/${s.id}`, { method: "PUT", body: { enabled: x } }); reload(); }} />}</td>
              <td style={{ textAlign: "right", whiteSpace: "nowrap" }}>
                <button className="btn small" onClick={() => switchStation(s.id)}>Umschalten</button>{" "}
                {admin && s.id !== "main" && <button className="btn small danger" onClick={async () => {
                  const c = window.prompt(`Sender „${s.name}“ mit allen Uhren, Sendeplänen und Sendenachweisen löschen? Zum Bestätigen das Kürzel „${s.id}“ eingeben:`);
                  if (c !== s.id) return;
                  try { await api(`/api/stations/${s.id}?confirm=${encodeURIComponent(String(s.id))}`, { method: "DELETE" }); reload(); } catch (e: any) { toast(e.message, true); }
                }}>Löschen</button>}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
      {add && (
        <Modal title="Neuer Sender" onClose={() => setAdd(false)}>
          <div className="form">
            <Field label="Name"><input type="text" value={nv.name} onChange={(e) => setNv({ ...nv, name: e.target.value })} autoFocus /></Field>
            <Field label="Zeitzone"><input type="text" value={nv.timezone} onChange={(e) => setNv({ ...nv, timezone: e.target.value })} /></Field>
            <Field label="Farbe"><input type="color" value={nv.color} onChange={(e) => setNv({ ...nv, color: e.target.value })} style={{ height: 34, width: 80 }} /></Field>
            <Field label="Übernehmen von">
              <select value={nv.copy_from ?? ""} onChange={(e) => setNv({ ...nv, copy_from: e.target.value || null })}>
                <option value="">– leer anfangen –</option>{(data ?? []).map((s) => <option key={s.id} value={s.id}>{s.name}</option>)}
              </select>
            </Field>
            {nv.copy_from && (
              <Field label="Programm" full hint="Empfohlen: gleiches Programm rund um die Uhr (Musik, Uhren, Sendeplan-Takt, Nachrichten), nur mit eigener Verpackung. Ausnahmen später als Regeln.">
                <Switch checked={nv.follow} onChange={(x) => setNv({ ...nv, follow: x })} label={nv.follow ? `Programm komplett von ${(data ?? []).find((s) => s.id === nv.copy_from)?.name ?? "dem Quellsender"} übernehmen` : "eigenes Programm (nur kopieren)"} />
              </Field>
            )}
            {nv.copy_from && (
              <div className="small muted" style={{ gridColumn: "1 / -1" }}>Verpackung (Jingles, Dropper, Sweeper, IDs, Pools, Imaging-Regeln) wird nie kopiert: Der neue Sender bekommt leere eigene Ordner dafür und du lädst seine Clips in der Bibliothek hoch.</div>
            )}
            {nv.copy_from && !nv.follow && (
              <div className="field full"><span>Was kopieren (danach unabhängig änderbar)</span>
                <div className="row wrap">
                  {[["music", "Musik-Zuordnung (Kategorien)"], ["clocks", "Uhren, Wochenraster, Regeln"], ["news", "Nachrichtenquellen"], ["settings", "Metadaten, Stille, Rotation, Klang"]].map(([k, l]) => (
                    <label key={k} className="badge" style={{ cursor: "pointer", background: nv.copy.includes(k) ? "var(--accent-2)" : undefined }}><input type="checkbox" checked={nv.copy.includes(k)} onChange={() => toggle(k)} /> {l}</label>
                  ))}
                </div>
              </div>
            )}
          </div>
          <p className="small muted">Der Server hat 2 Kerne: realistisch sind 4–6 Sender gleichzeitig. StereoTool wird nicht kopiert (Lizenz je Instanz) – neue Sender nutzen die eigene Klangbearbeitung.</p>
          <div className="row" style={{ marginTop: 12 }}><span className="spacer" /><button className="btn primary" disabled={!nv.name.trim() || busy} onClick={create}>{busy ? "Lege an …" : "Anlegen"}</button></div>
        </Modal>
      )}
    </div>
  );
}

function RuleEditor({ row, stations, onClose }: { row: any; stations: any[]; onClose: (changed: boolean) => void }) {
  const [v, setV] = useState<any>(row);
  const { data: programs } = useLoad<any[]>(v.target_station_id ? `/api/programs?station=${v.target_station_id}` : null, [v.target_station_id]);
  const save = async () => {
    try {
      if (v.id) await api(`/api/syndication/rules/${v.id}`, { method: "PUT", body: v });
      else await api("/api/syndication/rules", { body: v });
      toast("Regel gespeichert – der Zielsender plant die kommende Woche neu");
      onClose(true);
    } catch (e: any) {
      toast(e.message, true);
    }
  };
  const byProgram = !!v.program_id;
  return (
    <Modal title={v.id ? "Übernahmeregel" : "Neue Übernahmeregel"} onClose={() => onClose(false)}>
      <div className="form">
        <Field label="Name (optional)"><input type="text" value={v.name ?? ""} placeholder="z. B. Abendprogramm" onChange={(e) => setV({ ...v, name: e.target.value })} /></Field>
        <Field label="Quellsender (liefert die Musik)"><select value={v.source_station_id} onChange={(e) => setV({ ...v, source_station_id: e.target.value })}>{stations.map((s) => <option key={s.id} value={s.id}>{s.name}</option>)}</select></Field>
        <Field label="Zielsender (eigene Verpackung)"><select value={v.target_station_id} onChange={(e) => setV({ ...v, target_station_id: e.target.value, program_id: null })}>{stations.filter((s) => s.id !== v.source_station_id).map((s) => <option key={s.id} value={s.id}>{s.name}</option>)}</select></Field>
        <Field label="Wann">
          <select value={byProgram ? "program" : "time"} onChange={(e) => setV({ ...v, program_id: e.target.value === "program" ? programs?.[0]?.id ?? null : null })}>
            <option value="time">Wochentage und Uhrzeit</option><option value="program" disabled={!programs?.length}>Eine Sendung des Zielsenders</option>
          </select>
        </Field>
        {byProgram ? (
          <Field label="Sendung" full><select value={v.program_id} onChange={(e) => setV({ ...v, program_id: e.target.value })}>{(programs ?? []).map((p) => <option key={p.id} value={p.id}>{p.name} ({hhmm(p.start_min)}, {p.duration_min} min)</option>)}</select></Field>
        ) : (
          <>
            <div className="field full"><span>Tage</span>
              <div className="row wrap" style={{ gap: 4 }}>
                {ORDER.map((d) => <label key={d} className="badge" style={{ cursor: "pointer", background: v.days.includes(d) ? "var(--accent-2)" : undefined }}><input type="checkbox" checked={v.days.includes(d)} onChange={() => setV({ ...v, days: v.days.includes(d) ? v.days.filter((x: number) => x !== d) : [...v.days, d] })} /> {DAYS[d]}</label>)}
                <button type="button" className="btn small" onClick={() => setV({ ...v, days: [0, 1, 2, 3, 4, 5, 6] })}>ganze Woche</button>
                <button type="button" className="btn small" onClick={() => setV({ ...v, days: [1, 2, 3, 4, 5] })}>Mo–Fr</button>
                <button type="button" className="btn small" onClick={() => setV({ ...v, days: [6, 0] })}>Wochenende</button>
              </div>
            </div>
            <Field label="von"><input type="time" value={hhmm(v.start_min)} onChange={(e) => setV({ ...v, start_min: toMin(e.target.value) })} /></Field>
            <Field label="bis" hint="00:00 = Mitternacht (ganzer Tag: 00:00–00:00)"><input type="time" value={v.end_min >= 1440 ? "00:00" : hhmm(v.end_min)} onChange={(e) => setV({ ...v, end_min: toMin(e.target.value) || 1440 })} /></Field>
          </>
        )}
        <Field label="Zeitversatz (Minuten)" hint={`Ziel spielt ${shiftText(Number(v.shift_min) || 0)} als die Quelle. Beispiel: 120 = was die Quelle um 9 Uhr spielt, läuft beim Ziel um 11 Uhr.`}>
          <input type="number" step={15} value={v.shift_min} onChange={(e) => setV({ ...v, shift_min: Number(e.target.value) })} />
        </Field>
        <Field label="Gültig ab (optional)"><input type="date" value={v.valid_from ? String(v.valid_from).slice(0, 10) : ""} onChange={(e) => setV({ ...v, valid_from: e.target.value || null })} /></Field>
        <Field label="Gültig bis (optional)"><input type="date" value={v.valid_to ? String(v.valid_to).slice(0, 10) : ""} onChange={(e) => setV({ ...v, valid_to: e.target.value || null })} /></Field>
        <Field label="Aktiv" full><Switch checked={v.enabled !== false} onChange={(x) => setV({ ...v, enabled: x })} label={v.enabled !== false ? "an" : "aus"} /></Field>
      </div>
      <Tip>Der Zielsender spielt in dieser Zeit genau die Musikfolge des Quellsenders – mit eigenen Jingles, Werbung und Nachrichten. Am besten haben beide Uhren gleich viele Musikplätze; sonst füllt das Ziel mit eigener Rotation auf oder lässt die letzten Titel aus.</Tip>
      <div className="row" style={{ marginTop: 12 }}><span className="spacer" /><button className="btn" onClick={() => onClose(false)}>Abbrechen</button><button className="btn primary" onClick={save}>Speichern</button></div>
    </Modal>
  );
}

function Rules() {
  const me = useMe();
  const can = ["admin", "programm"].includes(me?.role);
  const { data: stations } = useLoad<any[]>("/api/stations");
  const { data: rules, reload } = useLoad<any[]>("/api/syndication/rules");
  const [edit, setEdit] = useState<any | null>(null);
  const fresh = () => ({ name: "", source_station_id: "main", target_station_id: stations?.find((s) => s.id !== "main")?.id ?? "", days: [0, 1, 2, 3, 4, 5, 6], start_min: 0, end_min: 1440, shift_min: 0, program_id: null, enabled: true });
  return (
    <div className="card">
      <div className="row" style={{ marginBottom: 8 }}>
        <h2 style={{ margin: 0 }}>Übernahmeregeln</h2><span className="spacer" />
        {can && <button className="btn primary" disabled={(stations?.length ?? 0) < 2} onClick={() => setEdit(fresh())}>+ Regel</button>}
      </div>
      {(stations?.length ?? 0) < 2 && <Tip>Zuerst unter „Sender“ einen zweiten Sender anlegen.</Tip>}
      {!rules?.length ? <Empty>Keine Regeln.</Empty> : (
        <table className="list">
          <thead><tr><th>Ziel</th><th>bekommt Musik von</th><th>Wann</th><th>Versatz</th><th>Gültig</th><th /></tr></thead>
          <tbody>
            {rules.map((r) => (
              <tr key={r.id} style={{ opacity: r.enabled ? 1 : 0.5 }}>
                <td><b>{r.target_name}</b>{r.name && <div className="small muted">{r.name}</div>}</td>
                <td>{r.source_name}</td>
                <td className="small">{r.program_id ? `Sendung „${r.program_name}“` : `${r.days.length === 7 ? "täglich" : ORDER.filter((d) => r.days.includes(d)).map((d) => DAYS[d]).join(", ")} ${r.start_min === 0 && r.end_min === 1440 ? "ganztägig" : `${hhmm(r.start_min)}–${r.end_min >= 1440 ? "24:00" : hhmm(r.end_min)}`}`}</td>
                <td className="small">{shiftText(r.shift_min)}</td>
                <td className="small">{r.valid_from || r.valid_to ? `${r.valid_from ? String(r.valid_from).slice(0, 10) : "…"} – ${r.valid_to ? String(r.valid_to).slice(0, 10) : "…"}` : "immer"}</td>
                <td style={{ textAlign: "right", whiteSpace: "nowrap" }}>
                  {can && <button className="btn small" onClick={() => setEdit(r)}>Bearbeiten</button>}{" "}
                  {can && <button className="btn small danger" onClick={async () => { if (window.confirm("Regel löschen? Der Zielsender plant die kommende Woche wieder mit eigener Musik.")) { await api(`/api/syndication/rules/${r.id}`, { method: "DELETE" }); reload(); } }}>✕</button>}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
      {edit && stations && <RuleEditor row={edit} stations={stations} onClose={(c) => { setEdit(null); if (c) reload(); }} />}
    </div>
  );
}

function Proof() {
  const [day, setDay] = useState(new Date().toISOString().slice(0, 10));
  const { data, reload } = useLoad<any[]>(`/api/network/proof?day=${day}`, [day]);
  const [open, setOpen] = useState<string | null>(null);
  const sum = useMemo(() => (data ?? []).reduce((a, r) => ({ s: a.s + r.source_count, p: a.p + r.played }), { s: 0, p: 0 }), [data]);
  return (
    <div className="card">
      <div className="row wrap" style={{ marginBottom: 8 }}>
        <h2 style={{ margin: 0 }}>Sendenachweis je Sender</h2><span className="spacer" />
        <input type="date" style={{ width: 160 }} value={day} onChange={(e) => setDay(e.target.value)} />
        <button className="btn small" onClick={reload}>Aktualisieren</button>
        <a className="btn" href={`/api/network/proof?day=${day}&format=csv`}>⇩ CSV</a>
      </div>
      <p className="small muted" style={{ marginTop: 0 }}>Vergleicht, was der Quellsender im Fenster gespielt hat, mit dem, was der Zielsender im (versetzten) Fenster wirklich gespielt hat. Insgesamt: {sum.p} von {sum.s} Titeln.</p>
      {!data?.length ? <Empty>An diesem Tag gelten keine Übernahmeregeln.</Empty> : (
        <table className="list">
          <thead><tr><th>Ziel</th><th>Quelle</th><th>Fenster</th><th>Gespielt</th><th>Status</th></tr></thead>
          <tbody>
            {data.map((r, i) => {
              const key = `${r.rule_id}-${i}`;
              return (
                <Fragment key={key}>
                  <tr onClick={() => setOpen(open === key ? null : key)} style={{ cursor: r.missing.length ? "pointer" : undefined }}>
                    <td><b>{r.target}</b>{r.name && <div className="small muted">{r.name}</div>}</td>
                    <td>{r.source}</td>
                    <td className="small">{hhmm(r.from_min)}–{r.to_min >= 1440 ? "24:00" : hhmm(r.to_min)} · {shiftText(r.shift_min)}</td>
                    <td>{r.played} / {r.source_count}</td>
                    <td><span className={`badge ${r.state === "vollständig" ? "ok" : r.state === "unvollständig" ? "bad" : ""}`}>{r.state}</span>{r.missing.length > 0 && <span className="small muted"> · {r.missing.length} fehlen ▾</span>}</td>
                  </tr>
                  {open === key && r.missing.map((m: any, j: number) => (
                    <tr key={`${key}-${j}`}><td /><td colSpan={4} className="small">fehlt: {m.artist} – {m.title} <span className="muted">(Quelle {new Date(m.at).toLocaleTimeString("de-DE", { hour: "2-digit", minute: "2-digit" })})</span></td></tr>
                  ))}
                </Fragment>
              );
            })}
          </tbody>
        </table>
      )}
    </div>
  );
}

/** All streams of all stations with their time windows and state; new stream for a chosen station. */
function Streams() {
  const { data: stations } = useLoad<any[]>("/api/stations");
  const { data: status } = usePoll<any[]>("/api/stations/status", 10_000);
  const [rows, setRows] = useState<any[] | null>(null);
  const load = async () => {
    const all: any[] = [];
    for (const st of stations ?? []) {
      try { for (const o of await api(`/api/outputs?station=${encodeURIComponent(st.id)}`)) all.push({ ...o, station: st }); } catch { /* no access */ }
    }
    setRows(all);
  };
  useEffect(() => { if (stations) void load(); }, [stations]);
  const DAYS_ = ["So", "Mo", "Di", "Mi", "Do", "Fr", "Sa"];
  const winText = (w: any[]) => (!w?.length ? "immer an" : w.map((x) => `${[1, 2, 3, 4, 5, 6, 0].filter((d) => x.days.includes(d)).map((d) => DAYS_[d]).join(" ")} ${hhmm(x.from)}–${x.to >= 1440 ? "24:00" : hhmm(x.to)}`).join(" · "));
  const state = (o: any) => status?.find((s) => s.id === o.station.id)?.outputs?.find((x: any) => x.name === o.name)?.state;
  return (
    <div className="card">
      <div className="row" style={{ marginBottom: 8 }}><h2 style={{ margin: 0 }}>Streams im Verbund</h2><span className="spacer" />
        <select value="" onChange={(e) => { if (e.target.value) switchStationTo(e.target.value, "#/einstellungen/ausgaenge"); }} style={{ width: 230 }}>
          <option value="">+ Stream für Sender …</option>{(stations ?? []).map((s) => <option key={s.id} value={s.id}>{s.name}</option>)}
        </select>
      </div>
      <Tip>Jeder Stream sendet das Programm seines Senders mit dessen Verpackung. Mit Zeitfenstern sendet er nur zu bestimmten Zeiten (z. B. Morningshow Mo–Fr 06:00–10:00); außerhalb ist der Mount frei. Zugangsdaten, Mount und Format trägst du beim Ausgang ein.</Tip>
      {!rows ? <div>Lade …</div> : !rows.length ? <Empty>Noch keine Streams.</Empty> : (
        <table className="list">
          <thead><tr><th>Stream</th><th>Sender</th><th>Mount</th><th>Sendezeiten</th><th>Status</th><th /></tr></thead>
          <tbody>
            {rows.map((o) => (
              <tr key={o.id} style={{ opacity: o.enabled ? 1 : 0.5 }}>
                <td><b>{o.name}</b><div className="small muted">{o.format?.toUpperCase()} {o.bitrate} kbit/s</div></td>
                <td>{o.station.name}</td>
                <td className="mono small">{o.host}:{o.port}{o.mount}</td>
                <td className="small">{winText(o.windows)}</td>
                <td className="small">{!o.enabled ? "aus" : o.windows?.length && !o.window_active ? `wartet${o.next_start ? ` bis ${new Date(o.next_start).toLocaleString("de-DE", { weekday: "short", hour: "2-digit", minute: "2-digit" })}` : ""}` : state(o) === "verbunden" ? <span className="badge ok">sendet</span> : state(o) === "fehler" ? <span className="badge bad">Fehler</span> : <span className="badge">{state(o) ?? "–"}</span>}</td>
                <td><button className="btn small" onClick={() => switchStationTo(o.station.id, "#/einstellungen/ausgaenge")}>Bearbeiten</button></td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </div>
  );
}

function CategorySwaps() {
  const { data: rows, reload } = useLoad<any[]>("/api/category-swaps");
  const { data: cats } = useLoad<any[]>("/api/categories");
  const [from, setFrom] = useState(""); const [to, setTo] = useState("");
  const [start, setStart] = useState("00:00"); const [end, setEnd] = useState("24:00");
  const [days, setDays] = useState<number[]>([0,1,2,3,4,5,6]);
  const save = async () => { try { await api("/api/category-swaps", { body: { from_category_id: from, to_category_id: to, start_min: toMin(start), end_min: end === "24:00" ? 1440 : toMin(end), days } }); reload(); } catch(e: any) { toast(e.message, true); } };
  return <div className="stack"><p>Beim gewählten Sender Verbund-Kategorien durch eigene Kategorien ersetzen. Neue Regeln gelten bei der nächsten Planung.</p><div className="row wrap">
    <select value={from} onChange={(e) => setFrom(e.target.value)}><option value="">Verbund-Kategorie</option>{cats?.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}</select>
    <select value={to} onChange={(e) => setTo(e.target.value)}><option value="">Ersatz-Kategorie</option>{cats?.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}</select>
    <input aria-label="Von" value={start} onChange={(e) => setStart(e.target.value)} /><input aria-label="Bis" value={end} onChange={(e) => setEnd(e.target.value)} />
    {ORDER.map((d) => <label key={d}><input type="checkbox" checked={days.includes(d)} onChange={(e) => setDays(e.target.checked ? [...days,d] : days.filter((x) => x!==d))} />{DAYS[d]}</label>)}
    <button className="btn" disabled={!from || !to} onClick={save}>Tausch hinzufügen</button></div>
    {rows?.map((r) => <div className="card row" key={r.id}>{r.from_name} → {r.to_name} · {hhmm(r.start_min)}–{r.end_min === 1440 ? "24:00" : hhmm(r.end_min)} · {r.days.map((d: number) => DAYS[d]).join(", ")}<button className="btn small" onClick={async () => { try { await api(`/api/category-swaps/${r.id}`, { method: "DELETE" }); reload(); } catch(e: any) { toast(e.message,true); } }}>Entfernen</button></div>)}</div>;
}
const TABS: [string, string][] = [["uebersicht", "Übersicht"], ["tausch", "Kategorie-Tausch"], ["regeln", "Regeln"], ["sender", "Sender"], ["streams", "Streams"], ["nachweis", "Sendenachweis"]];

export function NetworkPage({ tab = "uebersicht" }: { tab?: string }) {
  let body;
  switch (tab) {
    case "tausch": body = <CategorySwaps />; break;
    case "regeln": body = <Rules />; break;
    case "sender": body = <Stations />; break;
    case "nachweis": body = <Proof />; break;
    case "streams": body = <Streams />; break;
    default: body = <Overview />;
  }
  return (
    <div>
      <PageHead global title="Senderverbund" sub="Mehrere Sender, gleiche Musik, eigene Verpackung" />
      <div className="tabs">{TABS.map(([k, l]) => <a key={k} href={`#/verbund/${k}`} className={k === (tab || "uebersicht") ? "active" : ""}>{l}</a>)}</div>
      <HowTo text="Ein Zielsender übernimmt zu festgelegten Zeiten die Musikfolge eines Quellsenders – Titel für Titel – und spielt sie mit eigenen Jingles, Werbung und Nachrichten. Kein Durchreichen eines Streams." chapter="verbund" />
      {body}
    </div>
  );
}
